import { apiSlice } from './apiSlice';

/**
 * Platform credentials for Profile's two terminal sections, and cTrader Web.
 *
 *   GET /tradelocker/user-info/ → { tradelocker: { email, password, server },
 *                                   dxtrade: { email, password } }
 *
 * One request answers both pages despite the path — the keys are the platform
 * ids, and each page reads its own.
 *
 *   GET /is-ctrader/user/  → whether this trader gets "Launch cTrader" (N80)
 *   GET /redirect/ctrader/ → 302 to a signed cTrader Web session
 */
export const platformApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getPlatformCredentials: builder.query({
      query: () => '/tradelocker/user-info/',
    }),

    getCTraderAccess: builder.query({
      query: () => '/is-ctrader/user/',
    }),

    /**
     * The signed cTrader Web URL to frame. The endpoint answers with a redirect
     * rather than the URL, and a browser can't read a redirect's target — so
     * the request follows it, and the URL it finishes at is the answer (live
     * reads axios' `responseURL` the same way). Live also accepts `{ url }` or
     * a bare URL as the body, so those are read first.
     *
     * `credentials: 'omit'`: the redirect lands on cTrader's host, which would
     * have to allow credentialed requests from this origin for the response to
     * be readable at all; the bearer header is what authenticates the first
     * hop. Never cached — the URL is a one-off signed session.
     */
    getCTraderLaunchUrl: builder.query({
      query: () => ({ url: '/redirect/ctrader/', credentials: 'omit', responseHandler: 'text' }),
      transformResponse: (body, meta) => {
        const text = String(body ?? '').trim();
        if (text.startsWith('{')) {
          try {
            const url = JSON.parse(text)?.url;
            if (url) return url;
          } catch {
            /* not JSON after all — fall through to the redirect target */
          }
        }
        if (text.startsWith('http')) return text;
        const landed = meta?.response?.url;
        return landed && !landed.includes('/redirect/ctrader/') ? landed : null;
      },
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetPlatformCredentialsQuery,
  useGetCTraderAccessQuery,
  useGetCTraderLaunchUrlQuery,
} = platformApi;
